import React, { useState, useEffect, useCallback } from 'react';
import './ManageStudentAssignments.css';
import '../TeacherDashboard/MyStudents.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaUsers,
  FaSearch,
  FaTimes,
  FaSave,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaChalkboardTeacher,
} from 'react-icons/fa';

const studentName = (s) =>
  `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim() || s.email || '?';

const studentInitials = (s) => {
  const f = s.profile?.firstName?.[0] || '';
  const l = s.profile?.lastName?.[0] || '';
  return (f + l).toUpperCase() || '?';
};

const StudentGrid = ({ allStudents, selectedIds, onToggle, onSelectAll, onDeselectAll }) => {
  const [search, setSearch] = useState('');

  const filtered = allStudents
    .filter((s) => {
      if (!search) return true;
      const n = studentName(s).toLowerCase();
      const e = (s.email || '').toLowerCase();
      return n.includes(search.toLowerCase()) || e.includes(search.toLowerCase());
    })
    .sort((a, b) => studentName(a).localeCompare(studentName(b)));

  return (
    <div className="ms-student-grid-wrapper">
      <div className="ms-grid-toolbar">
        <div className="ms-search-box">
          <FaSearch className="ms-search-icon" />
          <input
            type="text"
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ms-search-input"
          />
          {search && (
            <button className="ms-search-clear" onClick={() => setSearch('')}>
              <FaTimes />
            </button>
          )}
        </div>
        <div className="ms-bulk-actions">
          <button className="ms-btn ms-btn-sm ms-btn-outline" onClick={onSelectAll}>
            All
          </button>
          <button className="ms-btn ms-btn-sm ms-btn-outline" onClick={onDeselectAll}>
            None
          </button>
        </div>
        <span className="ms-count-label">
          {selectedIds.length}/{allStudents.length} selected
        </span>
      </div>

      {filtered.length === 0 ? (
        <p className="ms-no-results">No students match your search.</p>
      ) : (
        <div className="ms-student-grid">
          {filtered.map((s) => {
            const id = String(s._id || s.id);
            const selected = selectedIds.includes(id);
            return (
              <div
                key={id}
                className={`ms-student-card ${selected ? 'selected' : ''}`}
                onClick={() => onToggle(id)}
              >
                <div className="ms-student-avatar">{studentInitials(s)}</div>
                <div className="ms-student-body">
                  <span className="ms-student-name">{studentName(s)}</span>
                  {s.batch && (
                    <span className="ms-student-batch-badge">Batch {s.batch}</span>
                  )}
                  {s.profile?.registrationNumber && (
                    <span className="ms-student-reg">{s.profile.registrationNumber}</span>
                  )}
                </div>
                <div className="ms-student-check">
                  {selected ? (
                    <FaCheckCircle className="ms-check-icon selected" />
                  ) : (
                    <div className="ms-check-circle" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ManageStudentAssignments = () => {
  const token = localStorage.getItem('token');

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const [teachers, setTeachers] = useState([]); // [{teacherId, name, isHOD, selectedStudentIds}]
  const [assignments, setAssignments] = useState({}); // { teacherId: string[] }
  const [students, setStudents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [expandedTeacherId, setExpandedTeacherId] = useState(null);
  const [savingTeacherId, setSavingTeacherId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [error, setError] = useState('');

  const selectedCourse = courses.find((c) => c._id === selectedCourseId);

  // Filter students to valid batches (start year >= currentYear-4)
  const minBatch = new Date().getFullYear() - 4;
  const validStudents = students.filter((s) => {
    if (!s.batch) return true;
    const startYear = parseInt(s.batch.split('-')[0]);
    return isNaN(startYear) || startYear >= minBatch;
  });

  const showSuccess = (msg) => {
    setSaveSuccess(msg);
    setTimeout(() => setSaveSuccess(''), 3000);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setCoursesLoading(true);
        const res = await fetch(`${collegeConfig.api.baseURL}/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) {
          setCourses(data.courses || []);
        } else {
          setError(data.message || 'Failed to load departments');
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load departments');
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, [token]);

  const fetchDetail = useCallback(async () => {
    if (!selectedCourseId) return;
    setDetailLoading(true);
    setError('');
    setExpandedTeacherId(null);
    try {
      const [teachersRes, studentsRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/teachers/admin/course-assignments/${selectedCourseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/students/by-course/${selectedCourseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const teachersData = await teachersRes.json();
      const studentsData = await studentsRes.json();

      if (teachersRes.ok) {
        setTeachers(teachersData.teachers || []);
        const map = {};
        (teachersData.teachers || []).forEach((t) => {
          map[t.teacherId] = t.selectedStudentIds || [];
        });
        setAssignments(map);
      } else {
        setError(teachersData.message || 'Failed to load faculty for this department');
      }

      if (studentsRes.ok) {
        const flattened = (studentsData.students || []).map((s) => ({
          ...s,
          batch: s.assignedCourses?.find((c) => c.courseId?.toString() === selectedCourseId)?.batch || ''
        }));
        setStudents(flattened);
      } else {
        setError(studentsData.message || 'Failed to load students for this department');
      }
    } catch (err) {
      console.error('Error fetching assignment detail:', err);
      setError('Failed to load department data');
    } finally {
      setDetailLoading(false);
    }
  }, [selectedCourseId, token]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const toggle = (teacherId, studentId) => {
    setAssignments((prev) => {
      const cur = prev[teacherId] || [];
      return {
        ...prev,
        [teacherId]: cur.includes(studentId)
          ? cur.filter((id) => id !== studentId)
          : [...cur, studentId]
      };
    });
  };

  const selectAll = (teacherId) => {
    setAssignments((prev) => ({
      ...prev,
      [teacherId]: validStudents.map((s) => String(s._id || s.id))
    }));
  };

  const deselectAll = (teacherId) => {
    setAssignments((prev) => ({ ...prev, [teacherId]: [] }));
  };

  const saveOneTeacher = async (teacherId) => {
    setSavingTeacherId(teacherId);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/admin/assign/${selectedCourseId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            assignments: [{ teacherId, selectedStudentIds: assignments[teacherId] || [] }]
          })
        }
      );
      if (res.ok) showSuccess('Saved');
    } catch (err) {
      console.error('Error saving teacher assignment:', err);
    } finally {
      setSavingTeacherId(null);
    }
  };

  const saveAllTeachers = async () => {
    setSavingAll(true);
    try {
      const payload = teachers.map((t) => ({
        teacherId: t.teacherId,
        selectedStudentIds: assignments[t.teacherId] || []
      }));
      const res = await fetch(
        `${collegeConfig.api.baseURL}/teachers/admin/assign/${selectedCourseId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ assignments: payload })
        }
      );
      if (res.ok) showSuccess('All assignments saved successfully');
    } catch (err) {
      console.error('Error saving all assignments:', err);
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="msa-container">
      <div className="msa-header">
        <h2><FaUsers style={{ marginRight: 10 }} />Manage Student Assignments</h2>
        <p className="msa-subtitle">
          Assign students to faculty for any department, regardless of the department's own assignment mode.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="msa-department-picker">
        <label htmlFor="msa-dept-select">Department</label>
        <select
          id="msa-dept-select"
          value={selectedCourseId}
          onChange={(e) => setSelectedCourseId(e.target.value)}
          disabled={coursesLoading}
        >
          <option value="">
            {coursesLoading ? 'Loading departments...' : 'Select a department'}
          </option>
          {courses.map((c) => (
            <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
          ))}
        </select>
      </div>

      {!selectedCourseId && !coursesLoading && (
        <div className="msa-empty-state">
          <p>Select a department above to view and assign its faculty and students.</p>
        </div>
      )}

      {selectedCourseId && detailLoading && (
        <div className="ms-loading">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      )}

      {selectedCourseId && !detailLoading && (
        <>
          {saveSuccess && <div className="ms-save-alert"><FaCheckCircle /> {saveSuccess}</div>}

          {teachers.length === 0 ? (
            <div className="msa-empty-state">
              <p>No faculty assigned to {selectedCourse?.name || 'this department'} yet.</p>
            </div>
          ) : (
            <>
              <div className="ms-accordion">
                {teachers.map((teacher) => {
                  const isExpanded = expandedTeacherId === teacher.teacherId;
                  const assigned = assignments[teacher.teacherId] || [];

                  return (
                    <div key={teacher.teacherId} className={`ms-accordion-card ${isExpanded ? 'expanded' : ''}`}>
                      <div
                        className="ms-accordion-header"
                        onClick={() => setExpandedTeacherId(isExpanded ? null : teacher.teacherId)}
                      >
                        <div className="ms-teacher-info">
                          <div className="ms-teacher-avatar">
                            <FaChalkboardTeacher />
                          </div>
                          <div>
                            <span className="ms-teacher-name">
                              {teacher.name}
                              {teacher.isHOD && <span className="ms-hod-chip">HOD</span>}
                            </span>
                            <span className="ms-teacher-assigned">
                              {assigned.length} / {validStudents.length} assigned
                            </span>
                          </div>
                        </div>
                        <div className="ms-accordion-controls">
                          <button
                            className="ms-btn ms-btn-sm ms-btn-primary"
                            onClick={(e) => { e.stopPropagation(); saveOneTeacher(teacher.teacherId); }}
                            disabled={savingTeacherId === teacher.teacherId}
                          >
                            <FaSave />
                            {savingTeacherId === teacher.teacherId ? 'Saving...' : 'Save'}
                          </button>
                          <span className="ms-accordion-chevron">
                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="ms-accordion-body">
                          <StudentGrid
                            allStudents={validStudents}
                            selectedIds={assigned}
                            onToggle={(id) => toggle(teacher.teacherId, id)}
                            onSelectAll={() => selectAll(teacher.teacherId)}
                            onDeselectAll={() => deselectAll(teacher.teacherId)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="ms-save-all-bar">
                <button
                  className="ms-btn ms-btn-primary ms-btn-lg"
                  onClick={saveAllTeachers}
                  disabled={savingAll}
                >
                  <FaSave /> {savingAll ? 'Saving All...' : 'Save All Assignments'}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ManageStudentAssignments;
